import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { usePermissoes } from '../utils/PermissoesContext';
import { getAuthToken, getTenantCpfCnpj } from '../utils/auth';

import FormularioOrcamentos from '../Formulario/FormularioOrcamentos';
import BASE_URL from '../utils/api';
import useRecarregarQuandoConcluir from '../utils/useRecarregarQuandoConcluir';
import { partesDataHora } from '../utils/dataBrasilia';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

// Função utilitária para chamadas da API com fallback
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  for (const url of urls) {
    try {
      console.log(`Tentando ${method} em: ${url}`);
      const config = {
        method,
        headers: {}
      };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      
      // Só adicionar Content-Type se não for um GET que pode retornar PDF
      if (method !== 'GET' || !endpoint.toLowerCase().includes('pdf')) {
        config.headers['Content-Type'] = 'application/json';
      }
      
      if (data && (method === 'POST' || method === 'PUT')) {
        config.body = JSON.stringify(data);
      }

      const response = await fetch(url, config);
      
      // Aceitar códigos de status de sucesso (200-299)
      if (response.ok || (response.status >= 200 && response.status < 300)) {
        console.log(`SUCESSO em: ${url} - Status: ${response.status}`);
        
        // Para DELETE ou status 204 (No Content)
        if (method === 'DELETE' || response.status === 204) {
          return { success: true, status: response.status };
        }
        
        // Verificar se há conteúdo para fazer parse JSON
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/pdf')) {
          console.log(`PDF detectado na resposta`);
          const pdfBlob = await response.blob();
          return { 
            success: true, 
            status: response.status, 
            type: 'application/pdf',
            data: pdfBlob,
            isPdf: true
          };
        } else if (contentType.includes('application/json')) {
          try {
            const jsonData = await response.json();
            // Verificar se o JSON contém dados de PDF em base64
            if (jsonData.arquivoPDFOrcamento || jsonData.ArquivoPDFOrcamento || jsonData.pdf) {
              return {
                ...jsonData,
                isPdf: true,
                type: 'application/pdf'
              };
            }
            return jsonData;
          } catch (jsonError) {
            console.log(`Erro ao parsear JSON, mas operação foi bem-sucedida`);
            return { success: true, status: response.status };
          }
        } else {
          // Resposta sem JSON
          const textData = await response.text();
          return { success: true, status: response.status, data: textData };
        }
      }
      console.log(`Falha em: ${url} - Status: ${response.status}`);
    } catch (error) {
      console.log(`Erro em: ${url} - ${error.message}`);
    }
  }
  
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}. Verifique se o backend está rodando.`);
};

export default function OrcamentoDropdown() {
  // Orçamento aprovado pelo link do cliente: recarrega a página sozinha
  useRecarregarQuandoConcluir('Orcamento/status-resumo', o => /aprovad|aceit/i.test(o.status || o.Status || ''));
  const navigate = useNavigate();
  const { temPermissao } = usePermissoes();
  const [showPopup, setShowPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showClientePopup, setShowClientePopup] = useState(false);
  const [showOportunidadePopup, setShowOportunidadePopup] = useState(false);
  const [showFormularioPopup, setShowFormularioPopup] = useState(false);
  
  const [orcamentoParaEditar, setOrcamentoParaEditar] = useState(null);
  const [orcamentos, setOrcamentos] = useState([]);
  const [orcamentosFiltrados, setOrcamentosFiltrados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [oportunidades, setOportunidades] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [oportunidadeSelecionada, setOportunidadeSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingOportunidades, setLoadingOportunidades] = useState(false);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [downloadOrcamentoId, setDownloadOrcamentoId] = useState(null);
  const [downloadResult, setDownloadResult] = useState(null); // result that may contain blob or base64
  const [showDownloadPopupShowDownloadButton, setShowDownloadPopupShowDownloadButton] = useState(true);
  const [showProgressAnimating, setShowProgressAnimating] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [manualDownloadRequested, setManualDownloadRequested] = useState(false);
  const [manualPopupClosedDuringAnim, setManualPopupClosedDuringAnim] = useState(false);
  const [autoDownloadActive, setAutoDownloadActive] = useState(false);
  
  // Estado para popup de compartilhar orçamento
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareOrcamentoId, setShareOrcamentoId] = useState(null);
  const [shareOrcamentoNomeCliente, setShareOrcamentoNomeCliente] = useState('');
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [emailEnviandoOrc, setEmailEnviandoOrc] = useState(false);
  const [statusPopupOrc, setStatusPopupOrc] = useState(null); // { type, title, text }
  
  // Estados para filtro
  const [filtro, setFiltro] = useState('clienteId');
  const [valorFiltro, setValorFiltro] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [error, setError] = useState('');

  const handleOpenPopup = () => {
    setError(''); // Limpar erro anterior
    setShowClientePopup(true);
    fetchClientes();
  };
  
  const handleSalvarSucesso = (resultado, options = {}) => {
    console.log('✅ Orçamento salvo com sucesso, avaliando popup de download');
    handleCloseAllPopups();
    const uploadedPdf = options.uploadedPdf || false;
    // Determinar id do orçamento
    const id = (resultado && (resultado.Id || resultado.id || resultado.orcamentoId || resultado.orcamento?.Id || resultado.orcamento?.id)) || null;
    // Se houve upload manual de PDF no fluxo de aprovação, não perguntar
    if (uploadedPdf) {
      console.log('📌 PDF enviado no fluxo, pulando popup de download');
      // Recarrega a lista em background (sem full reload)
      refreshOrcamentos();
      return;
    }

    // Se resultado indica PDF retornado no PUT (isPdf), baixar automaticamente e não perguntar
    if (resultado && resultado.isPdf) {
      console.log('📥 PUT retornou PDF; baixando automaticamente e mostrando popup sem botão');
      try {
        if (resultado.data instanceof Blob) {
          const blob = resultado.data;
          const fileName = `Orcamento_${id || 'download'}.pdf`;
          const urlBlob = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlBlob;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(urlBlob);
        } else if (resultado.ArquivoPDFOrcamento || resultado.arquivoPDFOrcamento || resultado.pdf) {
          const base64 = resultado.ArquivoPDFOrcamento || resultado.arquivoPDFOrcamento || resultado.pdf;
          const dataUrl = 'data:application/pdf;base64,' + base64;
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `Orcamento_${id || 'download'}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } catch (err) {
        console.warn('Erro ao baixar PDF retornado pelo PUT:', err);
      } finally {
        // Instead of returning immediately, show the popup stating success (no download button)
        setDownloadOrcamentoId(id);
        setDownloadResult(resultado);
        setShowDownloadPopupShowDownloadButton(false); // hide the Baixar PDF button when auto-downloaded
        setShowDownloadPopup(true);
        // SEM animação - popup permanece aberto até o usuário fechar
        setAutoDownloadActive(true);
      }

    }

    // Para edição sem PDF retornado, mostrar popup perguntando se deseja baixar
    if (id) {
      setDownloadOrcamentoId(id);
      setDownloadResult(resultado);
      setShowDownloadPopup(true);
    } else {
      // Sem id - apenas recarrega via estado
      refreshOrcamentos();
    }
  };

  const handleCloseAllPopups = () => {
    console.log('🔄 Fechando todos os popups e limpando seleções');
    setShowPopup(false);
    setShowEditPopup(false);
    setShowClientePopup(false);
    setShowOportunidadePopup(false);
    setShowFormularioPopup(false);
    setClienteSelecionado(null);
    setOportunidadeSelecionada(null);
    setOrcamentoParaEditar(null);
    setError('');
    setShowDownloadPopup(false);
      // reset manual download flags
      setManualDownloadRequested(false);
      setManualPopupClosedDuringAnim(false);
    setDownloadOrcamentoId(null);
    setDownloadResult(null);
    setShowDownloadPopupShowDownloadButton(true);
    setShowSharePopup(false);
    setShareOrcamentoId(null);
    setShareOrcamentoNomeCliente('');
    setLinkCopiado(false);
  };

  // Funções de compartilhar orçamento
  const getAprovacaoLink = (orcamentoId) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    // A página de aprovação precisa saber de qual empresa é o orçamento (o login guarda no token)
    const tenantCnpj =
      getTenantCpfCnpj() ||
      userInfo.tenantCNPJ ||
      userInfo.tenantCnpj ||
      userInfo?.tenant?.cnpj ||
      userInfo?.tenant?.CNPJ ||
      '';
    const query = tenantCnpj ? `?tenantCnpj=${encodeURIComponent(tenantCnpj)}` : '';
    return `${window.location.origin}/aprovacao/${orcamentoId}${query}`;
  };

  const handleCompartilhar = (orcamentoId, nomeCliente) => {
    setShareOrcamentoId(orcamentoId);
    setShareOrcamentoNomeCliente(nomeCliente);
    setLinkCopiado(false);
    setShowSharePopup(true);
  };

  const handleCopiarLink = () => {
    const link = getAprovacaoLink(shareOrcamentoId);
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    });
  };

  const handleCompartilharWhatsApp = () => {
    const link = getAprovacaoLink(shareOrcamentoId);
    const mensagem = `Olá! Segue o link para aprovação do orçamento #${shareOrcamentoId}:\n\n${link}\n\nPor favor, acesse o link para visualizar e aceitar as condições do orçamento.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  const handleCompartilharEmail = () => {
    const link = getAprovacaoLink(shareOrcamentoId);
    const assunto = `Aprovação do Orçamento #${shareOrcamentoId}`;
    const corpo = `Prezado(a) ${shareOrcamentoNomeCliente},\n\nSegue o link para aprovação do orçamento #${shareOrcamentoId}:\n\n${link}\n\nPor favor, acesse o link para visualizar e aceitar as condições do orçamento.\n\nAtenciosamente.`;
    window.open(`mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`, '_blank');
  };

  const handleEnviarEmailSMTPOrcamento = async () => {
    if (!shareOrcamentoId) return;
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const tenantCnpj =
      userInfo.tenantCNPJ ||
      userInfo.tenantCnpj ||
      userInfo?.tenant?.cnpj ||
      userInfo?.tenant?.CNPJ ||
      '';
    setEmailEnviandoOrc(true);
    const baseUrls = [
      `${BASE_URL}/AceiteOrcamento/enviar-email/${shareOrcamentoId}`,
      `${BASE_URL}/AceiteOrcamento/enviar-email/${shareOrcamentoId}`,
      `${BASE_URL}/AceiteOrcamento/enviar-email/${shareOrcamentoId}`,
      `${BASE_URL}/AceiteOrcamento/enviar-email/${shareOrcamentoId}`,
      `${BASE_URL}/AceiteOrcamento/enviar-email/${shareOrcamentoId}`
    ];
    let resultado = null;
    let ok = false;
    for (const url of baseUrls) {
      try {
        const qs = tenantCnpj ? `?tenantCnpj=${encodeURIComponent(tenantCnpj)}` : '';
        const token = getAuthToken();
        const headers = {};
        if (token) headers['Authorization'] = 'Bearer ' + token;
        const resp = await fetch(url + qs, { method: 'POST', headers });
        ok = resp.ok;
        try { resultado = await resp.json(); } catch (_) { resultado = null; }
        break;
      } catch (e) { continue; }
    }
    setEmailEnviandoOrc(false);
    const sucesso = ok && (
      resultado === null ||
      resultado?.sucesso === true ||
      resultado?.Sucesso === true
    );
    const msg = resultado?.mensagem || resultado?.Mensagem || resultado?.erro || resultado?.message;
    if (sucesso) {
      // Extrair o e-mail do cliente da mensagem retornada pelo backend ("Email enviado com sucesso para email@dominio.com.")
      const emailMatch = (resultado?.mensagem || '').match(/para\s+([^\s.]+@[^\s.]+\.[^\s.]+)/i);
      const emailCliente = emailMatch ? emailMatch[1] : '';
      const textoSucesso = emailCliente
        ? `E-mail enviado com sucesso!\n\nPara: ${emailCliente}`
        : (resultado?.mensagem || `Link de aprovação do orçamento #${shareOrcamentoId} enviado ao cliente${shareOrcamentoNomeCliente ? ` ${shareOrcamentoNomeCliente}` : ''} com sucesso.`);
      setStatusPopupOrc({ type: 'success', title: 'E-mail enviado!', text: textoSucesso, email: emailCliente });
    } else {
      setStatusPopupOrc({ type: 'error', title: 'Erro ao enviar', text: msg || 'Não foi possível enviar o e-mail. Verifique o backend.' });
    }
  };

  // Funções de filtro
  const handleFiltroChange = (novoFiltro) => {
    setFiltro(novoFiltro);
    setDropdownAberto(false);
    setValorFiltro('');
    // Resetar para todos os orçamentos quando muda o filtro
    setOrcamentosFiltrados(orcamentos);
  };

  const handleInputChange = (e) => {
    const valor = e.target.value;
    setValorFiltro(valor);
    aplicarFiltro(valor);
  };

  const aplicarFiltro = (valor) => {
    if (!valor.trim()) {
      setOrcamentosFiltrados(orcamentos);
      return;
    }

    const filtrados = orcamentos.filter(orcamento => {
      if (filtro === 'clienteId') {
        const clienteId = (orcamento.id_cliente || orcamento.Id_cliente || '').toString();
        return clienteId.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'oportunidadeId') {
        const oportunidadeId = (orcamento.oportunidadeId || orcamento.OportunidadeId || '').toString();
        return oportunidadeId.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'clienteNome') {
        const nomeCliente = orcamento.nomeCliente || orcamento.NomeCliente || '';
        return nomeCliente.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'orcamentoId') {
        const orcamentoId = (orcamento.id || orcamento.Id || '').toString();
        return orcamentoId.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'status') {
        const status = orcamento.status || orcamento.Status || '';
        return status.toLowerCase().includes(valor.toLowerCase());
      }
      return true;
    });

    setOrcamentosFiltrados(filtrados);
  };
  
  const handleEscolherCliente = (cliente) => {
    console.log('🔄 Cliente escolhido:', cliente);
    setClienteSelecionado(cliente);
    setShowClientePopup(false);
    setShowOportunidadePopup(true);
    const clienteId = cliente.id || cliente.Id;
    console.log('🔍 Buscando oportunidades para cliente ID:', clienteId);
    fetchOportunidadesPorCliente(clienteId);
  };
  
  const handleEscolherOportunidade = (oportunidade) => {
    console.log('🔄 Oportunidade escolhida:', oportunidade);
    console.log('🔄 Cliente atualmente selecionado:', clienteSelecionado);
    setOportunidadeSelecionada(oportunidade);
    setShowOportunidadePopup(false);
    setShowFormularioPopup(true);
    console.log('✅ Abrindo formulário com cliente e oportunidade pré-selecionados');
  };

  // Helper to perform actual download for currently selected orcamento
  const performDownloadForCurrentOrcamento = async () => {
    if (!downloadOrcamentoId) return;
    try {
      if (downloadResult && downloadResult.isPdf) {
        if (downloadResult.data instanceof Blob) {
          const blob = downloadResult.data;
          const fileName = `Orcamento_${downloadOrcamentoId || 'download'}.pdf`;
          const urlBlob = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlBlob;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(urlBlob);
        } else if (downloadResult.ArquivoPDFOrcamento || downloadResult.arquivoPDFOrcamento || downloadResult.pdf) {
          const base64 = downloadResult.ArquivoPDFOrcamento || downloadResult.arquivoPDFOrcamento || downloadResult.pdf;
          const dataUrl = 'data:application/pdf;base64,' + base64;
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `Orcamento_${downloadOrcamentoId || 'download'}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } else {
          await baixarPdfOrcamento(downloadOrcamentoId);
        }
      } else {
        await baixarPdfOrcamento(downloadOrcamentoId);
      }
      return true;
    } catch (err) {
      console.error('Erro ao efetuar download:', err);
      return false;
    }
  };

  // Handle closing the download popup by clicking overlay
  const handleDownloadPopupOverlayClick = (e) => {
    // Não fecha mais ao clicar no overlay - apenas pelos botões
    // handleCloseAllPopups();
  };

  const handleBarAnimationEnd = async () => {
    // Função mantida para compatibilidade, mas não é mais usada
  };
  
  const handleOpenEditPopup = async (orcamento) => {
    console.log('🔄 Abrindo orçamento para edição:', orcamento);

    // Passar apenas dados básicos - FormularioOrcamentos vai buscar dados completos
    const orcamentoBasico = {
      id: orcamento.id || orcamento.Id,
      Id: orcamento.id || orcamento.Id, // Para compatibilidade
      ...orcamento // Incluir outros dados básicos disponíveis
    };
    
    setOrcamentoParaEditar(orcamentoBasico);
    setShowEditPopup(true);
  };

  const fetchOrcamentoCompleto = async (orcamentoId) => {
    try {
      console.log('🔍 Buscando orçamento completo, ID:', orcamentoId);
      const orcamentoCompleto = await apiCall(`Orcamento/${orcamentoId}`);
      console.log('✅ Orçamento completo retornado:', orcamentoCompleto);
      return orcamentoCompleto;
    } catch (error) {
      console.error('❌ Erro ao buscar orçamento completo:', error);
      return null;
    }
  };

  // Helper to reload orcamentos without a full page reload
  const refreshOrcamentos = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Atualizando lista de orçamentos...');
      const data = await apiCall('Orcamento');
      const orcamentosArray = Array.isArray(data) ? data : [];
      setOrcamentos(orcamentosArray);
      setOrcamentosFiltrados(orcamentosArray);
    } catch (err) {
      console.error('❌ Erro ao atualizar orçamentos:', err);
      setError(`Erro ao carregar orçamentos: ${err.message}`);
      setOrcamentos([]);
      setOrcamentosFiltrados([]);
    } finally {
      setLoading(false);
    }
  };

  // Buscar orçamentos do backend - inicial e quando certos popups fecham
  useEffect(() => {
    refreshOrcamentos();
    fetchClientes();
  }, [showEditPopup, showFormularioPopup]); // Atualiza ao fechar qualquer popup de edição

  // Notificar AjudaWidget sobre estado do formulário de orçamento
  useEffect(() => {
    const formularioAberto = showFormularioPopup || showEditPopup;
    const outroPopupAberto = showClientePopup || showOportunidadePopup || showSharePopup || showDownloadPopup;
    window.dispatchEvent(new CustomEvent('orcamento-popup-state', {
      detail: { formularioAberto, outroPopupAberto }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('orcamento-popup-state', {
        detail: { formularioAberto: false, outroPopupAberto: false }
      }));
    };
  }, [showFormularioPopup, showEditPopup, showClientePopup, showOportunidadePopup, showSharePopup, showDownloadPopup]);

  // Listener para fechar todos os popups a pedido do AjudaWidget
  useEffect(() => {
    const handler = () => handleCloseAllPopups();
    window.addEventListener('orcamento-fechar-todos-popups', handler);
    return () => window.removeEventListener('orcamento-fechar-todos-popups', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplicar filtro quando orcamentos ou valorFiltro mudarem
  useEffect(() => {
    aplicarFiltro(valorFiltro);
  }, [orcamentos, filtro]);

  const fetchClientes = async () => {
    try {
      setLoadingClientes(true);
      console.log('🔍 Iniciando busca de clientes...');
      
      const data = await apiCall('Cliente');
      console.log('📥 Resposta bruta da API:', data);
      console.log('📊 Tipo dos dados:', typeof data);
      console.log('📊 É array?', Array.isArray(data));
      
      if (data && Array.isArray(data)) {
        console.log('📊 Quantidade total de registros:', data.length);
        
        // Filtrar apenas clientes com dados válidos
        const clientesValidos = data.filter(cliente => {
          const id = cliente.id || cliente.Id;
          const nome = cliente.nome || cliente.Nome;
          const hasValidData = id && nome && nome.trim() !== '';
          
          if (!hasValidData) {
            console.log('⚠️ Cliente inválido encontrado:', cliente);
          }
          
          return hasValidData;
        });
        
        console.log('✅ Clientes válidos filtrados:', clientesValidos.length);
        
        if (clientesValidos.length > 0) {
          console.log('👤 Exemplos de clientes válidos:');
          clientesValidos.slice(0, 3).forEach((cliente, index) => {
            console.log(`   ${index + 1}. ID: ${cliente.id || cliente.Id}, Nome: ${cliente.nome || cliente.Nome}, Email: ${cliente.email || cliente.Email}`);
          });
        }
        
        setClientes(clientesValidos);
      } else {
        console.log('⚠️ Dados inválidos recebidos da API');
        setClientes([]);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      setError(`Erro ao carregar clientes: ${error.message}`);
      setClientes([]);
    } finally {
      setLoadingClientes(false);
      console.log('🏁 Busca de clientes finalizada');
    }
  };

  const fetchOportunidadesPorCliente = async (clienteId) => {
    try {
      setLoadingOportunidades(true);
      console.log('🔍 Carregando oportunidades para cliente:', clienteId);
      const data = await apiCall('Oportunidade');
      console.log('📋 Todas as oportunidades:', data);
      console.log('📊 Quantidade total de oportunidades:', data?.length || 0);
      
      if (data && Array.isArray(data)) {
        // Filtra oportunidades pelo ID do cliente
        const oportunidadesDoCliente = data.filter(op => {
          const clienteIdDaOportunidade = op.clienteId || op.ClienteId;
          console.log(`🔗 Oportunidade ${op.id || op.Id}: clienteId=${clienteIdDaOportunidade}, comparando com ${clienteId}`);
          return clienteIdDaOportunidade === clienteId || clienteIdDaOportunidade === parseInt(clienteId);
        });
        
        console.log('✅ Oportunidades filtradas:', oportunidadesDoCliente);
        console.log('📊 Quantidade filtrada:', oportunidadesDoCliente.length);
        
        if (oportunidadesDoCliente.length > 0) {
          console.log('🎯 Primeira oportunidade filtrada:', oportunidadesDoCliente[0]);
        }
        
        setOportunidades(oportunidadesDoCliente);
      } else {
        console.log('⚠️ Nenhuma oportunidade encontrada ou dados inválidos');
        setOportunidades([]);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar oportunidades:', error);
      setOportunidades([]);
    } finally {
      setLoadingOportunidades(false);
    }
  };

  // Helper to download PDF for Orcamento by id, with fallback URLs
  const baixarPdfOrcamento = async (id) => {
    if (!id) return alert('ID de orçamento inválido.');
    const urls = [
      `${BASE_URL}/Orcamento/download-pdf/${id}`,
      `${BASE_URL}/Orcamento/download-pdf/${id}`,
      `${BASE_URL}/Orcamento/download-pdf/${id}`,
      `${BASE_URL}/Orcamento/download-pdf/${id}`,
      `${BASE_URL}/Orcamento/download-pdf/${id}`,
      `${BASE_URL}/Orcamento/${id}/pdf`,
      `${BASE_URL}/Orcamento/${id}/pdf`,
      `${BASE_URL}/Orcamento/${id}/pdf`,
      `${BASE_URL}/Orcamento/${id}/pdf`,
      `${BASE_URL}/Orcamento/${id}/pdf`
    ];

    for (const url of urls) {
      try {
        const resp = await fetch(url, { method: 'GET' });
        if (resp.ok) {
          const blob = await resp.blob();
          if (!blob || blob.size === 0) {
            alert('PDF não disponível para este Orçamento.');
            return;
          }

          const dispo = resp.headers.get('Content-Disposition') || '';
          let fileName = `Orcamento_${id}.pdf`;
          const match = /filename\*=UTF-8''(.+)$/.exec(dispo) || /filename="?([^";]+)"?/.exec(dispo);
          if (match && match[1]) fileName = decodeURIComponent(match[1]);

          const urlBlob = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlBlob;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(urlBlob);
          return;
        }
      } catch (error) {
        console.warn('Erro ao tentar baixar PDF do orçamento em', url, error.message || error);
      }
    }

    alert('Não foi possível baixar o PDF do orçamento. Verifique se o backend está rodando ou se o PDF existe.');
  };

  const obterNomeCliente = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const formatarNomeCliente = (nome) => {
    if (!nome || nome.length <= 12) {
      return <span style={{ fontSize: 16 }}>{nome}</span>;
    }
    
    // Quebra o nome respeitando os espaços
    const palavras = nome.split(' ');
    let linhaAtual = '';
    const linhas = [];
    
    for (const palavra of palavras) {
      const testeLinhaAtual = linhaAtual ? `${linhaAtual} ${palavra}` : palavra;
      
      if (testeLinhaAtual.length <= 12) {
        linhaAtual = testeLinhaAtual;
      } else {
        if (linhaAtual) {
          linhas.push(linhaAtual);
          linhaAtual = palavra;
        } else {
          // Se uma palavra sozinha for maior que 12 caracteres, mantém ela inteira
          linhas.push(palavra);
          linhaAtual = '';
        }
      }
    }
    
    if (linhaAtual) {
      linhas.push(linhaAtual);
    }
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {linhas.map((linha, index) => (
          <span key={index} style={{ fontSize: 16 }}>{linha}</span>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      width: '100vw',
      maxWidth: '100vw',
      minWidth: '100vw',
      background: '#e0e0e0',
      minHeight: 'calc(100vh - 0px)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      overflowY: 'auto',
    }}>
      {/* POPUP MODAL NOVO ORÇAMENTO */}
      {showPopup && (
        <div className="orcamento-popup-overlay" onClick={handleCloseAllPopups} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 340,
            maxWidth: 540,
            width: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <button
              onClick={handleCloseAllPopups}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              aria-label="Fechar popup"
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            <FormularioOrcamentos />
          </div>
        </div>
      )}

      {/* POPUP MODAL EDITAR ORÇAMENTO */}
      {showEditPopup && (
        <div className="orcamento-popup-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 340,
            maxWidth: 900,
            width: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <button
              onClick={handleCloseAllPopups}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              aria-label="Fechar popup de edição"
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            <h2 style={{ 
              color: 'rgb(235, 133, 0)', 
              marginBottom: '20px', 
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '24px'
            }}>
              Editar Orçamento #{orcamentoParaEditar?.id || orcamentoParaEditar?.Id}
            </h2>
            <FormularioOrcamentos 
              orcamentoParaEditar={orcamentoParaEditar} 
              onSalvarSucesso={handleSalvarSucesso}
              onCancelar={handleCloseAllPopups}
              onCompartilhar={(id, nome) => handleCompartilhar(id, nome)}
            />
          </div>
        </div>
      )}

        {/* POPUP: oferecer download de PDF após edição */}
        {showDownloadPopup && (
          <div className="orcamento-popup-overlay" onClick={handleDownloadPopupOverlayClick} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.18)',
            zIndex: 100001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 8px 32px 0 #00000022',
              padding: 32,
              minWidth: 300,
              maxWidth: 540,
              width: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <h3 style={{ color: 'rgb(235, 133, 0)', marginBottom: 8 }}>Orçamento atualizado com sucesso</h3>
              <p style={{ textAlign: 'center' }}>
                {showDownloadPopupShowDownloadButton ? 'Novo PDF gerado. Deseja baixar o PDF agora?' : 'Orçamento atualizado com sucesso. Novo PDF gerado e baixado automaticamente.'}
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                {showDownloadPopupShowDownloadButton && (
                <button className="btn-baixar-contrato" onClick={async () => {
                  if (downloadInProgress) return; // avoid double click
                  setDownloadInProgress(true);
                  try {
                    await performDownloadForCurrentOrcamento();
                    setShowDownloadPopup(false); // Fecha popup após baixar
                    refreshOrcamentos();
                  } finally {
                    setDownloadInProgress(false);
                  }
                }}>{downloadInProgress ? 'Baixando...' : 'Baixar PDF'}</button>
                )}
                <button className="btn-editar-contrato" disabled={downloadInProgress} onClick={(e) => {
                  e.stopPropagation();
                  setShowDownloadPopup(false);
                  refreshOrcamentos();
                }}>Fechar</button>
              </div>
            </div>
          </div>
        )}

      {/* POPUP 1: LISTA DE CLIENTES */}
      {showClientePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div className="orcamento-popup-cliente" style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 600,
            maxWidth: 800,
            width: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative',
          }}>
            <button
              onClick={handleCloseAllPopups}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            <h2 style={{ 
              color: 'rgb(235, 133, 0)', 
              marginBottom: '20px', 
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '24px'
            }}>
              Selecionar Cliente
            </h2>
            <div className="clientes-lista-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {loadingClientes ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#666', 
                  padding: '40px',
                  fontSize: '16px'
                }}>
                  <div style={{ marginBottom: '10px' }}>🔄</div>
                  Carregando clientes...
                </div>
              ) : error ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#d32f2f', 
                  padding: '40px',
                  fontSize: '16px',
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  border: '1px solid #f8bbd9'
                }}>
                  <div style={{ marginBottom: '10px' }}>⚠️</div>
                  {error}
                  <br />
                  <button
                    onClick={() => {
                      setError('');
                      fetchClientes();
                    }}
                    style={{
                      marginTop: '15px',
                      background: 'rgb(235, 133, 0)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer'
                    }}
                  >
                    Tentar Novamente
                  </button>
                </div>
              ) : clientes.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#888', 
                  padding: '40px',
                  fontSize: '16px'
                }}>
                  <div style={{ marginBottom: '10px' }}>📋</div>
                  Nenhum cliente cadastrado ainda.
                  <br />
                  <small style={{ color: '#666', marginTop: '10px', display: 'block' }}>
                    Cadastre clientes primeiro para criar orçamentos.
                  </small>
                </div>
              ) : (
                clientes.map((cliente) => {
                  // Verificar se o cliente tem dados válidos
                  const clienteId = cliente.id || cliente.Id;
                  const clienteNome = cliente.nome || cliente.Nome || 'Nome não informado';
                  const clienteEmail = cliente.email || cliente.Email || 'Email não informado';
                  
                  // Só renderizar se tiver ID válido
                  if (!clienteId) return null;
                  
                  return (
                    <div
                      key={clienteId}
                      className="cliente-card-popup"
                      style={{
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '2px solid #e9ecef',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                        e.currentTarget.style.borderColor = 'rgb(235, 133, 0)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.borderColor = '#e9ecef';
                      }}
                    >
                      <div className="cliente-card-info" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#666',
                          fontWeight: '600'
                        }}>
                          <span style={{ color: 'rgb(235, 133, 0)' }}>ID:</span> {clienteId}
                        </div>
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold',
                          color: '#333',
                          marginBottom: '4px'
                        }}>
                          👤 {clienteNome}
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>📧</span> {clienteEmail}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEscolherCliente(cliente);
                        }}
                        style={{
                          background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 4px 12px rgba(235, 133, 0, 0.3)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, rgb(250, 178, 65) 0%, rgb(235, 133, 0) 100%)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Selecionar
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* POPUP 2: LISTA DE OPORTUNIDADES */}
      {showOportunidadePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div className="orcamento-popup-oportunidade" style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 600,
            maxWidth: 800,
            width: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative',
          }}>
            {/* Botão de voltar para escolher cliente */}
            <button
              onClick={() => {
                setShowOportunidadePopup(false);
                setShowClientePopup(true);
              }}
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
              title="Voltar para escolher cliente"
            >
              ←
            </button>
            <button
              onClick={handleCloseAllPopups}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            <h2 style={{ 
              color: 'rgb(235, 133, 0)', 
              marginBottom: '10px', 
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '24px'
            }}>
              Selecionar Oportunidade
            </h2>
            <p style={{ 
              textAlign: 'center', 
              marginBottom: '20px', 
              color: '#666',
              fontSize: '16px'
            }}>
              Cliente selecionado: <strong>{clienteSelecionado?.nome}</strong>
            </p>
            <div className="oportunidades-lista-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {loadingOportunidades ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  Carregando oportunidades...
                </div>
              ) : oportunidades.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <div style={{ color: '#888', fontSize: '16px' }}>
                    Nenhuma oportunidade encontrada para este cliente.
                  </div>
                  {temPermissao('Oportunidade') && (
                    <button
                      onClick={() => {
                        handleCloseAllPopups();
                        navigate('/sidebar/OportunidadeDropdown', { 
                          state: { 
                            autoOpenForm: true, 
                            clientePreSelecionado: clienteSelecionado 
                          } 
                        });
                      }}
                      style={{
                        background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '14px 28px',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 12px rgba(235, 133, 0, 0.3)',
                        letterSpacing: '0.3px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgb(250, 178, 65) 0%, rgb(235, 133, 0) 100%)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      Criar Oportunidade para este Cliente
                    </button>
                  )}
                </div>
              ) : (
                oportunidades.map((oportunidade) => {
                  const id = oportunidade.id || oportunidade.Id;
                  const titulo = oportunidade.titulo || oportunidade.Titulo || oportunidade.Oportunidade_Titulo || oportunidade.descricao || oportunidade.Descricao || `Oportunidade ${id}`;
                  const valor = oportunidade.valor || oportunidade.Valor || 0;
                  const status = oportunidade.status || oportunidade.Status || 'Não definido';
                  const descricao = oportunidade.descricao || oportunidade.Descricao || oportunidade.Oportunidade_Descricao || 'Sem descrição';
                  const dataVencimento = oportunidade.dataVencimento || oportunidade.DataVencimento || oportunidade.Data_Vencimento;
                  
                  return (
                    <div
                      key={id}
                      className="oportunidade-card-popup"
                      style={{
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '2px solid #e9ecef',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgb(235, 133, 0)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(235, 133, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e9ecef';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      <div className="oportunidade-card-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div className="oportunidade-card-info" style={{ flex: 1, marginRight: '16px' }}>
                          <div style={{ 
                            fontSize: '18px', 
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px',
                            lineHeight: '1.2'
                          }}>
                            🎯 {titulo}
                          </div>
                          
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                            gap: '8px',
                            marginBottom: '12px'
                          }}>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span>🆔</span> <strong>ID:</strong> {id}
                            </div>
                            
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span>💰</span> <strong>Valor:</strong> R$ {parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span>📊</span> <strong>Status:</strong> 
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                backgroundColor: status.toLowerCase() === 'ativo' ? '#c8e6c9' : 
                                               status.toLowerCase() === 'pendente' ? '#fff3e0' : 
                                               status.toLowerCase() === 'finalizado' ? '#bbdefb' : '#f5f5f5',
                                color: status.toLowerCase() === 'ativo' ? '#2e7d32' : 
                                       status.toLowerCase() === 'pendente' ? 'rgb(208, 116, 0)' : 
                                       status.toLowerCase() === 'finalizado' ? '#1976d2' : '#666'
                              }}>
                                {status}
                              </span>
                            </div>
                            
                            {dataVencimento && (
                              <div style={{ 
                                fontSize: '14px', 
                                color: '#666',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <span>📅</span> <strong>Vencimento:</strong> {new Date(dataVencimento).toLocaleDateString('pt-BR')}
                              </div>
                            )}
                          </div>
                          
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#666',
                            lineHeight: '1.4',
                            backgroundColor: '#f8f9fa',
                            padding: '8px',
                            borderRadius: '6px',
                            borderLeft: '3px solid rgb(235, 133, 0)'
                          }}>
                            <strong>📝 Descrição:</strong> {descricao}
                          </div>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEscolherOportunidade(oportunidade);
                          }}
                          style={{
                            background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(235, 133, 0, 0.3)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, rgb(250, 178, 65) 0%, rgb(235, 133, 0) 100%)';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(208, 116, 0) 100%)';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          Selecionar
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* POPUP 3: FORMULÁRIO ORÇAMENTOS */}
      {showFormularioPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 340,
            maxWidth: 900,
            width: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            {/* Botão de voltar para escolher oportunidade */}
            <button
              onClick={() => {
                setShowFormularioPopup(false);
                setShowOportunidadePopup(true);
              }}
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
              title="Voltar para escolher oportunidade"
            >
              ←
            </button>
            <button
              onClick={handleCloseAllPopups}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            <h2 style={{ 
              color: 'rgb(235, 133, 0)', 
              marginBottom: '20px', 
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '24px'
            }}>
              Criar Novo Orçamento
            </h2>
            <FormularioOrcamentos 
              clienteSelecionado={clienteSelecionado}
              oportunidadeSelecionada={oportunidadeSelecionada}
              onSalvarSucesso={handleSalvarSucesso}
            />
          </div>
        </div>
      )}

      <MobileAddListener onOpen={handleOpenPopup} />

      <div className="orcamento-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, margin: '32px 0 24px 0' }}>
        <span style={{ color: 'rgb(235, 133, 0)', fontWeight: 800, fontSize: 26, letterSpacing: 1, textAlign: 'center', flex: 'none', textTransform: 'uppercase' }}>
          NOVO ORÇAMENTO:
        </span>
        <button
          onClick={handleOpenPopup}
          className="btn-adicionar-orcamento"
          style={{
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgb(235, 133, 0)',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 0.18s',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '600',
            gap: '8px'
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgb(208, 116, 0)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
          aria-label="Adicionar novo orçamento"
        >
          <span style={{ fontSize: '20px', lineHeight: 1 }}>+</span>
          <span>Adicionar</span>
        </button>
      </div>

      {/* Campo de filtro */}
      <div className="orcamento-filtro-container" style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 24px auto',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: '0 20px',
        justifyContent: 'center'
      }}>
        <label style={{ 
          color: '#333', 
          fontSize: '16px', 
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}>
          Filtrar por:
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
              type="text"
              style={{
                padding: '10px 15px',
                paddingRight: valorFiltro ? '40px' : '15px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '14px',
                minWidth: '250px',
                outline: 'none'
              }}
              placeholder={`Filtrar por ${
                filtro === 'clienteId' ? 'ID do Cliente' :
                filtro === 'oportunidadeId' ? 'ID da Oportunidade' :
                filtro === 'clienteNome' ? 'Nome do Cliente' :
                filtro === 'orcamentoId' ? 'ID do Orçamento' :
                filtro === 'status' ? 'Status' : filtro
              }`}
              value={valorFiltro}
              onChange={handleInputChange}
            />
            {valorFiltro && (
              <button
                onClick={() => setValorFiltro('')}
                title="Limpar filtro"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  color: 'rgb(235, 133, 0)',
                  border: 'none',
                  width: '20px',
                  height: '20px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  lineHeight: '1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onFocus={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                  e.target.style.outline = 'none';
                }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setDropdownAberto(!dropdownAberto)}
              style={{
                padding: '10px 15px',
                backgroundColor: 'rgb(235, 133, 0)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '200px',
                justifyContent: 'space-between'
              }}
            >
              {`Filtrar por ${
                filtro === 'clienteId' ? 'ID do Cliente' :
                filtro === 'oportunidadeId' ? 'ID da Oportunidade' :
                filtro === 'clienteNome' ? 'Nome do Cliente' :
                filtro === 'orcamentoId' ? 'ID do Orçamento' :
                filtro === 'status' ? 'Status' : filtro
              }`}
              <span style={{ transform: dropdownAberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {dropdownAberto && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'rgb(235, 133, 0)',
                border: '1px solid rgb(235, 133, 0)',
                borderRadius: '5px',
                marginTop: '2px',
                zIndex: 100001,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div 
                  onClick={() => handleFiltroChange('clienteId')}
                  style={{ 
                    padding: '10px 15px', 
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Filtrar por ID do Cliente
                </div>
                <div 
                  onClick={() => handleFiltroChange('oportunidadeId')}
                  style={{ 
                    padding: '10px 15px', 
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Filtrar por ID da Oportunidade
                </div>
                <div 
                  onClick={() => handleFiltroChange('clienteNome')}
                  style={{ 
                    padding: '10px 15px', 
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Filtrar por Nome do Cliente
                </div>
                <div 
                  onClick={() => handleFiltroChange('orcamentoId')}
                  style={{ 
                    padding: '10px 15px', 
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Filtrar por ID do Orçamento
                </div>
                <div 
                  onClick={() => handleFiltroChange('status')}
                  style={{ 
                    padding: '10px 15px', 
                    cursor: 'pointer',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Filtrar por Status
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Listagem de Orçamentos em Cards */}
      <div className="orcamentos-cards-container" style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 24px auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        alignItems: 'center',
        overflowX: 'hidden',
      }}>
        {/* Loading State */}
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            color: 'rgb(235, 133, 0)', 
            fontSize: 18, 
            marginTop: 32,
            padding: '20px'
          }}>
            Carregando orçamentos...
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <div style={{ 
            textAlign: 'center', 
            color: '#d32f2f', 
            fontSize: 16, 
            marginTop: 32,
            padding: '20px',
            backgroundColor: '#ffe6e6',
            borderRadius: '8px',
            border: '1px solid #ff9999'
          }}>
            {error}
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && orcamentosFiltrados.length === 0 && orcamentos.length > 0 && (
          <div style={{ 
            textAlign: 'center', 
            color: '#888', 
            fontSize: 18, 
            marginTop: 32 
          }}>
            Nenhum orçamento encontrado com o filtro aplicado.
          </div>
        )}
        
        {!loading && !error && orcamentos.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            color: '#888', 
            fontSize: 18, 
            marginTop: 32 
          }}>
            Nenhum orçamento encontrado.
          </div>
        )}
        
        {/* Orçamentos Cards */}
        {!loading && !error && orcamentosFiltrados.map((orc, idx) => {
          // Extração de dados com mapeamento correto do backend
          const orcamentoId = orc.id || orc.Id || '-';
          const clienteId = orc.id_cliente || orc.Id_cliente || '-';
          const oportunidadeId = orc.oportunidadeId || orc.OportunidadeId || '-';
          const nomeCliente = orc.nomeCliente || orc.NomeCliente || 'Cliente não informado';
          const tituloOportunidade = orc.tituloOportunidade || orc.TituloOportunidade || 'Oportunidade não informada';
          // Valor total (custo + lucro − desconto); orçamentos antigos sem preco_final usam o Valor
          const valor = orc.preco_final || orc.Preco_final || orc.valor || orc.Valor || 0;
          const status = orc.status || orc.Status || 'Sem status';
          const dataCriacao = orc.dataCriacao || orc.DataCriacao || '-';
          const validadeOrcamento = orc.validadeOrcamento || orc.ValidadeOrcamento || '-';
          const descricaoOrcamento = orc.descricaoOrcamento || orc.DescricaoOrcamento || 'Sem descrição';

          return (
            <div
              key={orcamentoId}
              className="orcamento-card"
              style={{
                background: '#e0e0e0',
                borderRadius: 0,
                boxShadow: 'none',
                padding: '14px 36px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                position: 'relative',
                minHeight: 60,
                minWidth: 1150,
                maxWidth: '100%',
                gap: 36,
                margin: 0,
                borderBottom: '1px solid #111',
              }}
            >
              {/* Bloco de dados em linha */}
              <div className="orcamento-card-fields" style={{ display: 'flex', flexDirection: 'row', gap: 24, flex: 1 }}>
                
                {/* ID do Orçamento */}
                <div className="orcamento-field orcamento-field-id" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 80 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>ID Orçamento</span>
                  <span style={{ fontSize: 16 }}>{orcamentoId}</span>
                </div>
                
                {/* Nome do Cliente */}
                <div className="orcamento-field orcamento-field-cliente" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 150 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>Cliente</span>
                  <div style={{ fontSize: 16, maxWidth: '150px', wordWrap: 'break-word' }}>
                    {formatarNomeCliente(nomeCliente)}
                  </div>
                </div>
                
                {/* Título da Oportunidade */}
                <div className="orcamento-field orcamento-field-oportunidade" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 130 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>Oportunidade</span>
                  <div style={{ fontSize: 16, maxWidth: '130px', wordWrap: 'break-word' }}>
                    {tituloOportunidade.length > 20 ? `${tituloOportunidade.substring(0, 20)}...` : tituloOportunidade}
                  </div>
                </div>
                
                {/* Valor */}
                <div className="orcamento-field orcamento-field-valor" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 100 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>Valor</span>
                  <span style={{ fontSize: 16, color: '#2e7d32', fontWeight: '600' }}>
                    {valor > 0 ? `R$ ${parseFloat(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00'}
                  </span>
                </div>
                
                {/* Status */}
                <div className="orcamento-field orcamento-field-status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 100 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>Status</span>
                  <span style={{ 
                    fontSize: 14, 
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: (status === 'Aprovado' || status === 'Aceito') ? '#c8e6c9' : 
                                   status === 'Pendente' ? '#fff3e0' : 
                                   status === 'Rejeitado' ? '#ffcdd2' : '#f5f5f5',
                    color: (status === 'Aprovado' || status === 'Aceito') ? '#2e7d32' : 
                           status === 'Pendente' ? 'rgb(208, 116, 0)' : 
                           status === 'Rejeitado' ? '#d32f2f' : '#666'
                  }}>
                    {status}
                  </span>
                </div>
                
                {/* Data Criação */}
                <div className="orcamento-field orcamento-field-data" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 120 }}>
                  <span style={{ fontWeight: 700, color: '#444', fontSize: 15, marginBottom: 2 }}>Data Criação</span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 16 }}>
                      {partesDataHora(dataCriacao).data}
                    </span>
                    <span style={{ fontSize: 14, color: '#666' }}>
                      {partesDataHora(dataCriacao).hora}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Botões de Ação - Layout horizontal */}
              <div className="orcamento-card-actions" style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center', marginLeft: 20, marginTop: -15 }}>
                
                {/* Botão PDF - Versão para Dados Binários do Banco */}
                <button
                  style={{
                    background: 'rgb(235, 133, 0)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 20px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(235, 133, 0, 0.27)',
                    transition: 'background 0.18s',
                    minWidth: '100px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgb(208, 116, 0)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
                  onClick={async () => {
                    try {
                      console.log(`🔄 ===== BAIXANDO PDF PARA ORÇAMENTO ID: ${orcamentoId} =====`);
                      console.log(`📋 Dados do card do orçamento:`, {
                        id: orcamentoId,
                        cliente: nomeCliente,
                        valor: valor,
                        status: status
                      });
                      
                      // Primeiro, vamos tentar buscar os dados do orçamento diretamente
                      let orcamentoData = null;
                      
                      // Tentar buscar o orçamento específico primeiro
                      const getEndpoints = [
                        `Orcamento/${orcamentoId}`,
                        `Orcamento`
                      ];
                      
                      const baseUrls = [
                        BASE_URL,
                        BASE_URL, 
                        'http://localhost:44371/api',
                        'https://localhost:44371/api',
                        'http://localhost:64918/api'
                      ];
                      
                      // Buscar dados do orçamento específico
                      console.log(`🔍 Buscando dados do orçamento ID: ${orcamentoId}...`);
                      for (const endpoint of getEndpoints) {
                        for (const baseUrl of baseUrls) {
                          try {
                            const url = `${baseUrl}/${endpoint}`;
                            console.log(`🌐 Tentando buscar dados em: ${url}`);
                            
                            const response = await fetch(url, {
                              method: 'GET',
                              headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json'
                              }
                            });
                            
                            if (response.ok) {
                              const data = await response.json();
                              console.log(`📊 Resposta recebida de ${endpoint}:`, data);
                              
                              if (Array.isArray(data)) {
                                // É uma lista, encontrar o orçamento específico pelo ID
                                orcamentoData = data.find(o => {
                                  const orcId = o.id || o.Id || o.ID;
                                  console.log(`🔍 Comparando orçamento ID ${orcId} com ${orcamentoId}`);
                                  return orcId == orcamentoId;
                                });
                                if (orcamentoData) {
                                  console.log(`✅ Orçamento ID ${orcamentoId} encontrado na lista!`);
                                }
                              } else if ((data.id || data.Id || data.ID) == orcamentoId) {
                                // É o orçamento específico
                                orcamentoData = data;
                                console.log(`✅ Orçamento ID ${orcamentoId} encontrado diretamente!`);
                              }
                              
                              if (orcamentoData) {
                                console.log(`📄 Dados completos do orçamento ID ${orcamentoId}:`, orcamentoData);
                                break;
                              }
                            } else {
                              console.log(`❌ Erro HTTP ${response.status} em: ${url}`);
                            }
                          } catch (error) {
                            console.log(`❌ Erro ao buscar dados em ${baseUrl}/${endpoint}:`, error.message);
                          }
                        }
                        if (orcamentoData) break;
                      }
                      
                      if (!orcamentoData) {
                        throw new Error(`Não foi possível encontrar os dados do orçamento ID: ${orcamentoId}`);
                      }
                      
                      // Verificar se tem PDF nos dados
                      const pdfField = orcamentoData.arquivoPDFOrcamento || 
                                     orcamentoData.ArquivoPDFOrcamento ||
                                     orcamentoData.pdf ||
                                     orcamentoData.PDF ||
                                     orcamentoData.arquivo_pdf_orcamento;
                      
                      console.log(`🔍 Verificando campo PDF no orçamento ID ${orcamentoId}:`);
                      console.log(`   - arquivoPDFOrcamento: ${orcamentoData.arquivoPDFOrcamento ? 'SIM' : 'NÃO'}`);
                      console.log(`   - ArquivoPDFOrcamento: ${orcamentoData.ArquivoPDFOrcamento ? 'SIM' : 'NÃO'}`);
                      console.log(`   - pdf: ${orcamentoData.pdf ? 'SIM' : 'NÃO'}`);
                      console.log(`   - Campo encontrado:`, pdfField ? `SIM (tipo: ${typeof pdfField})` : 'NÃO');
                      
                      if (pdfField) {
                        let pdfBlob = null;
                        
                        if (typeof pdfField === 'string') {
                          console.log(`📝 PDF como string - Tamanho: ${pdfField.length} caracteres`);
                          
                          try {
                            // Tentar como base64
                            let base64Data = pdfField;
                            
                            // Remover prefixos se existirem
                            base64Data = base64Data.replace(/^data:application\/pdf;base64,/, '');
                            base64Data = base64Data.replace(/^data:.*base64,/, '');
                            
                            console.log(`  Tentando decodificar base64 - Tamanho: ${base64Data.length}`);
                            
                            const binaryString = atob(base64Data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                              bytes[i] = binaryString.charCodeAt(i);
                            }
                            pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                            
                            console.log(`📦 Blob criado a partir de base64 - Tamanho: ${pdfBlob.size} bytes`);
                            
                          } catch (base64Error) {
                            console.log(`❌ Erro no base64, tentando como texto binário:`, base64Error.message);
                            
                            // Tentar como string binária direta
                            const bytes = new Uint8Array(pdfField.length);
                            for (let i = 0; i < pdfField.length; i++) {
                              bytes[i] = pdfField.charCodeAt(i);
                            }
                            pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                            console.log(`📦 Blob criado a partir de string binária - Tamanho: ${pdfBlob.size} bytes`);
                          }
                          
                        } else if (pdfField instanceof Array || pdfField instanceof Uint8Array) {
                          console.log(`  PDF como array - Tamanho: ${pdfField.length} bytes`);
                          
                          // É um array de bytes
                          const bytes = new Uint8Array(pdfField);
                          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                          console.log(`📦 Blob criado a partir de array - Tamanho: ${pdfBlob.size} bytes`);
                          
                        } else {
                          console.log(`🔍 PDF em formato desconhecido:`, typeof pdfField);
                          
                          // Tentar converter para string e depois para bytes
                          const pdfString = String(pdfField);
                          const bytes = new Uint8Array(pdfString.length);
                          for (let i = 0; i < pdfString.length; i++) {
                            bytes[i] = pdfString.charCodeAt(i);
                          }
                          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                          console.log(`📦 Blob criado a partir de conversão - Tamanho: ${pdfBlob.size} bytes`);
                        }
                        
                        if (pdfBlob && pdfBlob.size > 0) {
                          // Fazer o download
                          const fileURL = URL.createObjectURL(pdfBlob);
                          const link = document.createElement('a');
                          link.href = fileURL;
                          link.download = `Orcamento_${orcamentoId}_${new Date().toISOString().split('T')[0]}.pdf`;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(fileURL);
                          
                          console.log(`✅ PDF do orçamento ID ${orcamentoId} baixado com sucesso! Tamanho: ${pdfBlob.size} bytes`);
                          alert(`PDF do orçamento ID ${orcamentoId} baixado com sucesso!`);
                          return;
                          
                        } else {
                          console.log(`❌ Blob PDF vazio ou inválido`);
                        }
                      }
                      
                      // Se chegou até aqui, tentar o endpoint de PDF diretamente
                      console.log(`🔄 Tentando endpoint direto de PDF...`);
                      
                      for (const baseUrl of baseUrls) {
                        try {
                          const pdfUrl = `${baseUrl}/Orcamento/${orcamentoId}/pdf`;
                          console.log(`🌐 Tentando URL direta: ${pdfUrl}`);
                          
                          const response = await fetch(pdfUrl, {
                            method: 'GET',
                            headers: {
                              'Accept': 'application/pdf'
                            }
                          });
                          
                          if (response.ok) {
                            const contentType = response.headers.get('Content-Type') || '';
                            console.log(`  Content-Type direto: ${contentType}`);
                            
                            if (contentType.includes('application/pdf')) {
                              const pdfBlob = await response.blob();
                              console.log(`📦 PDF Blob direto - Tamanho: ${pdfBlob.size} bytes`);
                              
                              if (pdfBlob.size > 0) {
                                const fileURL = URL.createObjectURL(pdfBlob);
                                const link = document.createElement('a');
                                link.href = fileURL;
                                link.download = `Orcamento_${orcamentoId}_${new Date().toISOString().split('T')[0]}.pdf`;
                                link.style.display = 'none';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(fileURL);
                                
                                console.log(`✅ PDF do orçamento ID ${orcamentoId} baixado via endpoint direto!`);
                                alert(`PDF do orçamento ID ${orcamentoId} baixado com sucesso!`);
                                return;
                              }
                            }
                          }
                        } catch (directError) {
                          console.log(`❌ Erro no endpoint direto: ${directError.message}`);
                        }
                      }
                      
                      throw new Error(`Não foi possível baixar o PDF do orçamento ID ${orcamentoId} por nenhum método`);
                      
                    } catch (error) {
                      console.error(`❌ ERRO FINAL para orçamento ID ${orcamentoId}:`, error);
                      alert(`Erro ao baixar PDF do orçamento ID ${orcamentoId}: ${error.message}\n\nVerifique o console (F12) para mais detalhes.`);
                    }
                  }}
                >
                  Baixar PDF
                </button>
                
                {/* Botão Editar - Laranja */}
                <button
                  style={{
                    background: 'rgb(235, 133, 0)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 20px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(235, 133, 0, 0.27)',
                    transition: 'background 0.18s',
                    minWidth: '85px'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgb(208, 116, 0)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
                  onClick={() => handleOpenEditPopup(orc)}
                  title="Editar orçamento"
                >
                  Editar
                </button>

                {/* Botão Compartilhar */}
                <button
                  style={{
                    background: '#25D366',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    boxShadow: '0 3px 10px #25D36644',
                    transition: 'background 0.18s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1EBE5A'}
                  onMouseLeave={e => e.currentTarget.style.background = '#25D366'}
                  onClick={() => handleCompartilhar(orcamentoId, nomeCliente)}
                  title="Compartilhar link de aprovação"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                </button>
                
              </div>
            </div>
          );
        })}
      </div>

      {/* Espaço extra para o fundo branco se estender para baixo */}
      <div style={{ flex: 1 }} />

      {/* POPUP COMPARTILHAR ORÇAMENTO */}
      {showSharePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          zIndex: 100001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 32px 0 #00000022',
            padding: 32,
            minWidth: 340,
            maxWidth: 540,
            width: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <button
              onClick={() => { setShowSharePopup(false); setShareOrcamentoId(null); setLinkCopiado(false); }}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                background: 'rgb(235, 133, 0)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(235, 133, 0, 0.27)',
                transition: 'background 0.18s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgb(250, 178, 65)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgb(235, 133, 0)'}
            >
              ×
            </button>
            
            <h3 style={{ color: 'rgb(235, 133, 0)', marginBottom: 8, fontSize: 22 }}>
              Compartilhar Orçamento #{shareOrcamentoId}
            </h3>
            <p style={{ textAlign: 'center', color: '#555', marginBottom: 16 }}>
              Envie o link de aprovação para o cliente. Ao acessar o link, o cliente poderá visualizar e aceitar as condições do orçamento.
            </p>

            {/* Link gerado */}
            <div style={{
              width: '100%',
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              wordBreak: 'break-all'
            }}>
              <span style={{ flex: 1, fontSize: 13, color: '#333', fontFamily: 'monospace' }}>
                {getAprovacaoLink(shareOrcamentoId)}
              </span>
              <button
                onClick={handleCopiarLink}
                style={{
                  background: linkCopiado ? '#4caf50' : '#9e9e9e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => { if (!linkCopiado) e.currentTarget.style.background = 'rgb(235, 133, 0)'; }}
                onMouseLeave={e => { if (!linkCopiado) e.currentTarget.style.background = '#9e9e9e'; }}
              >
                {linkCopiado ? '✓ Copiado!' : 'Copiar'}
              </button>
            </div>

            {/* Botões de compartilhamento */}
            <div style={{ display: 'flex', gap: 16, width: '100%', justifyContent: 'center' }}>
              {/* WhatsApp */}
              <button
                onClick={handleCompartilharWhatsApp}
                title="Enviar pelo WhatsApp"
                style={{
                  background: '#fff',
                  border: '2px solid #25D366',
                  borderRadius: 12,
                  width: 72,
                  height: 72,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  boxShadow: '0 2px 8px #25D36622',
                  transition: 'all 0.18s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e8faf0'; e.currentTarget.style.boxShadow = '0 4px 14px #25D36644'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 2px 8px #25D36622'; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                  <circle cx="16" cy="16" r="16" fill="#25D366"/>
                  <path fill="#fff" d="M23.5 8.5A9.93 9.93 0 0 0 16.02 5C10.48 5 5.99 9.49 5.99 15.02c0 1.77.46 3.5 1.34 5.02L6 26l6.1-1.6a10.03 10.03 0 0 0 4.82 1.23h.01c5.53 0 10.02-4.49 10.02-10.02 0-2.68-1.04-5.19-2.93-7.08l-.52-.03zM16.02 24.24h-.01a8.33 8.33 0 0 1-4.24-1.16l-.3-.18-3.13.82.83-3.05-.2-.32a8.34 8.34 0 0 1-1.28-4.44c0-4.6 3.75-8.35 8.36-8.35 2.23 0 4.33.87 5.9 2.45a8.3 8.3 0 0 1 2.45 5.91c-.01 4.6-3.75 8.32-8.38 8.32zm4.59-6.24c-.25-.13-1.48-.73-1.71-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.16-.29.18-.54.06-.25-.13-1.06-.39-2.01-1.24-.74-.66-1.24-1.48-1.39-1.73-.14-.25-.01-.38.11-.5.11-.1.25-.27.37-.41.12-.14.16-.24.25-.4.08-.16.04-.3-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43l-.48-.01c-.16 0-.43.06-.66.31-.22.25-.86.84-.86 2.05s.88 2.38 1 2.54c.12.16 1.73 2.64 4.2 3.7.59.25 1.05.4 1.4.52.59.19 1.12.16 1.55.1.47-.07 1.46-.6 1.66-1.17.21-.57.21-1.06.15-1.17-.06-.1-.22-.16-.47-.28z"/>
                </svg>
                <span style={{ fontSize: 10, color: '#25D366', fontWeight: 700 }}>WhatsApp</span>
              </button>

              {/* E-mail */}
              <button
                onClick={handleEnviarEmailSMTPOrcamento}
                disabled={emailEnviandoOrc}
                title={`Enviar link de aprovação por e-mail ao cliente (SMTP)`}
                style={{
                  background: '#fff',
                  border: '2px solid #1976d2',
                  borderRadius: 12,
                  width: 72,
                  height: 72,
                  cursor: emailEnviandoOrc ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  opacity: emailEnviandoOrc ? 0.7 : 1,
                  boxShadow: '0 2px 8px #1976d222',
                  transition: 'all 0.18s'
                }}
                onMouseEnter={e => { if (!emailEnviandoOrc) { e.currentTarget.style.background = '#e3f0fd'; e.currentTarget.style.boxShadow = '0 4px 14px #1976d244'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 2px 8px #1976d222'; }}
              >
                {emailEnviandoOrc ? (
                  <span style={{ fontSize: 22 }}>⏳</span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                    <circle cx="16" cy="16" r="16" fill="#1976d2"/>
                    <rect x="7" y="10" width="18" height="12" rx="2" fill="none" stroke="#fff" strokeWidth="1.5"/>
                    <polyline points="7,10 16,17 25,10" fill="none" stroke="#fff" strokeWidth="1.5"/>
                  </svg>
                )}
                <span style={{ fontSize: 10, color: '#1976d2', fontWeight: 700 }}>{emailEnviandoOrc ? 'Enviando...' : 'E-mail'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />

      {/* Status Popup SMTP Orçamento */}
      {statusPopupOrc && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)', zIndex: 100002,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '36px 32px 28px',
            minWidth: 320, maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
            textAlign: 'center', position: 'relative'
          }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {statusPopupOrc.type === 'success' ? '✅' : statusPopupOrc.type === 'info' ? 'ℹ️' : '❌'}
            </div>
            <div style={{
              fontWeight: 700, fontSize: 20, marginBottom: 10,
              color: statusPopupOrc.type === 'success' ? '#1a7a3c' : statusPopupOrc.type === 'info' ? '#1565c0' : '#c62828'
            }}>
              {statusPopupOrc.title}
            </div>
            {statusPopupOrc.type === 'success' && statusPopupOrc.email ? (
              <div style={{ marginBottom: 18 }}>
                <div style={{ color: '#555', fontSize: 14, marginBottom: 6 }}>E-mail do cliente:</div>
                <div style={{
                  background: '#e8f5e9', borderRadius: 8, padding: '8px 16px',
                  color: '#1a7a3c', fontWeight: 700, fontSize: 15,
                  border: '1px solid #a5d6a7', wordBreak: 'break-all'
                }}>
                  {statusPopupOrc.email}
                </div>
              </div>
            ) : (
              <div style={{ color: '#444', fontSize: 15, marginBottom: 18, lineHeight: 1.5 }}>
                {statusPopupOrc.text}
              </div>
            )}
            <button
              onClick={() => setStatusPopupOrc(null)}
              style={{
                background: statusPopupOrc.type === 'success' ? '#1a7a3c' : statusPopupOrc.type === 'info' ? '#1565c0' : '#c62828',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer'
              }}
            >OK</button>
          </div>
        </div>
      )}
    </div>
  );
}
